import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { Subscription, Observable } from 'rxjs';
import { DatePipe } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { CookieService } from 'ngx-cookie-service';
import { ToastrService } from 'ngx-toastr';
import { BrokerService } from 'src/app/services/broker.services';
import { TemaService } from 'src/app/services/tema.services';
import { SseService } from 'src/app/services/SseService';
import { BrokerTimeService } from 'src/app/services/broker-time.service';
import { BrokerAdapterFactory } from 'src/app/services/broker-adapter-factory.service';
import { OrdenesSyncService } from 'src/app/services/ordenes-sync.service';
import { ColaOrdenesService } from 'src/app/services/cola-ordenes.service';
import { SlTpBrokerService, SlTpContexto } from 'src/app/services/sltp-broker.service';
import { SalidaOperacionBrokerService, SalidaPendiente } from 'src/app/services/salida-operacion-broker.service';
import { etiquetaOrigenOrden } from 'src/app/shared/pipes/origen-orden.pipe';
import { AccionesOrdenBrokerService } from 'src/app/services/acciones-orden-broker.service';
import { ConfirmDialogComponent } from '../../common/confirm-dialog/confirm-dialog.component';
import { esOrdenOco, isooperDeOrderRef, ladoOco, sentidoDeOrderRef, tipoOrdenMt } from 'src/app/shared/utils/order-ref.util';
import { agregaTiporModelo, flattenArray, formatFechaHoraCotizacion } from '../cartera-utils';

/** Estados de orden que ya no están vivas en el broker (IBKR y MetaAPI): van al Historial. */
const ESTADOS_CERRADOS = ['Filled', 'Cancelled', 'Canceled', 'Inactive', 'Sincro',
  'ORDER_STATE_FILLED', 'ORDER_STATE_CANCELED', 'ORDER_STATE_REJECTED', 'ORDER_STATE_EXPIRED'];
const ESTADOS_FILLED = ['Filled', 'ORDER_STATE_FILLED'];
const ESTADOS_CANCELADOS = ['Cancelled', 'Canceled', 'ORDER_STATE_CANCELED'];
const CLAVE_COLUMNAS = 'opMtColumnas';

type Pestana = 'operaciones' | 'historial';

/**
 * Columna de una pestaña. `pos` / `orden` dan el valor por el que se ordena una fila de posición o de
 * orden (sin accessor la columna no se ordena para ese tipo de fila).
 */
interface ColumnaMt {
  id: string;
  nombre: string;
  visible: boolean;
  txt?: boolean;
  pos?: (p: any) => any;
  orden?: (o: any) => any;
}

/**
 * Fila del Historial: una posición (isooper|A/B) con su entrada, su SL y TP y su salida, como en MT.
 * Las órdenes sin order_ref van solas.
 */
interface FilaHistorial {
  clave: string;
  entrada: any;
  sl?: any;
  tp?: any;
  /** Orden con la que se cerró: la salida a mercado o el SL/TP ejecutado. */
  salida?: any;
  porSl: boolean;
  porTp: boolean;
  ordenes: any[];
}

/**
 * Operaciones de la cartera del broker al estilo de MetaTrader: posiciones abiertas y órdenes pendientes; pestaña Historial con las órdenes ejecutadas o canceladas.
 * Convive con app-lista-ordenes hasta reemplazarla.
 */
@Component({
  selector: 'app-operaciones-mt',
  templateUrl: './operaciones-mt.component.html',
  styleUrl: './operaciones-mt.component.scss'
})
export class OperacionesMtComponent implements OnInit, OnDestroy {
  /** Alto total del componente (tabla + pestañas). */
  @Input() alto: string | null = null;
  /** En Control Operativa (sin efecto aún; se mantiene por paridad con lista-ordenes). */
  @Input() controlOperativa = false;
  /** Mostrar como pendientes las órdenes que esperan en la cola de envío. */
  @Input() mostrarCola = false;
  /** Órdenes recibidas de broker/listaordenes en cada carga. */
  @Output() ordenesCargadas = new EventEmitter<any[]>();

  pestana: Pestana = 'operaciones';
  isDark = false;
  brokOptimaWallet: any;

  /** Filas ya ordenadas para la plantilla. */
  posiciones: any[] = [];
  pendientes: any[] = [];
  historial: FilaHistorial[] = [];
  filaSeleccionada: any;
  /** isooper de las posiciones con un cierre en curso. */
  cerrando = new Set<string>();
  colPanelOpen = false;

  columnas: Record<Pestana, ColumnaMt[]> = {
    operaciones: [
      { id: 'simbolo', nombre: 'Símbolo', visible: false, txt: true, pos: p => p.nombreCompania, orden: o => o.ticker },
      { id: 'id', nombre: 'ID', visible: true, txt: true, pos: p => p.isooper, orden: o => o.companyName },
      { id: 'ticket', nombre: 'Ticket', visible: true, txt: true, pos: p => Number(this.ticket(p)), orden: o => Number(o.orderId) },
      { id: 'fecha', nombre: 'Fecha/Hora', visible: true, txt: true, pos: p => Number(p.lastExecutionTimer) || 0, orden: o => Number(o.fechaRegistro) || 0 },
      { id: 'tipo', nombre: 'Tipo', visible: true, txt: true, pos: p => p.bajista ? 'sell' : 'buy', orden: o => this.tipoOrden(o) },
      { id: 'volumen', nombre: 'Volumen', visible: true, pos: p => Number(p.cantidadacciones), orden: o => Number(o.quantity) },
      { id: 'precio', nombre: 'Precio', visible: true, pos: p => Number(p.precioentradareal), orden: o => Number(o.price) },
      { id: 'sl', nombre: 'S / L', visible: true, pos: p => Number(this.sl(p)) || 0, orden: o => Number(this.slOrden(o)) || 0 },
      { id: 'tp', nombre: 'T / P', visible: true, pos: p => Number(this.tp(p)) || 0, orden: o => Number(this.tpOrden(o)) || 0 },
      { id: 'actual', nombre: 'Precio', visible: true, pos: p => Number(p.cotizacion) },
      { id: 'beneficio', nombre: 'Beneficio', visible: true, pos: p => Number(p.valorlatente), orden: o => o.status },
      { id: 'origen', nombre: 'Origen', visible: true, txt: true, pos: p => this.origenPosicion(p), orden: o => etiquetaOrigenOrden(o.origen) }
    ],
    historial: [
      { id: 'fecha', nombre: 'Fecha/Hora', visible: true, txt: true, orden: (f: FilaHistorial) => this.fechaOrden(f.entrada) },
      { id: 'ticket', nombre: 'Ticket', visible: true, txt: true, orden: (f: FilaHistorial) => Number(f.entrada.orderId) },
      { id: 'simbolo', nombre: 'Símbolo', visible: false, txt: true, orden: (f: FilaHistorial) => f.entrada.ticker },
      { id: 'id', nombre: 'ID', visible: true, txt: true, orden: (f: FilaHistorial) => f.entrada.companyName },
      { id: 'tipo', nombre: 'Tipo', visible: true, txt: true, orden: (f: FilaHistorial) => this.tipoOrden(f.entrada) },
      { id: 'volumen', nombre: 'Volumen', visible: true, orden: (f: FilaHistorial) => Number(this.volumen(f.entrada)) },
      { id: 'precio', nombre: 'Precio', visible: true, orden: (f: FilaHistorial) => Number(this.precioEjec(f.entrada)) },
      { id: 'sl', nombre: 'S / L', visible: true, orden: (f: FilaHistorial) => Number(this.slHistorial(f)) || 0 },
      { id: 'tp', nombre: 'T / P', visible: true, orden: (f: FilaHistorial) => Number(this.tpHistorial(f)) || 0 },
      { id: 'fechaCierre', nombre: 'Fecha/Hora cierre', visible: true, txt: true, orden: (f: FilaHistorial) => f.salida ? this.fechaOrden(f.salida) : 0 },
      { id: 'precioCierre', nombre: 'Precio cierre', visible: true, orden: (f: FilaHistorial) => f.salida ? Number(this.precioEjec(f.salida)) : 0 },
      { id: 'estado', nombre: 'Estado', visible: true, txt: true, orden: (f: FilaHistorial) => this.estadoHistorial(f) },
      { id: 'origenEntrada', nombre: 'Origen entrada', visible: true, txt: true, orden: (f: FilaHistorial) => etiquetaOrigenOrden(f.entrada.origen) },
      { id: 'origenSalida', nombre: 'Origen salida', visible: true, txt: true, orden: (f: FilaHistorial) => f.salida ? etiquetaOrigenOrden(f.salida.origen) : '' }
    ]
  };
  visibles: Record<Pestana, ColumnaMt[]> = { operaciones: [], historial: [] };
  /** Orden por cabecera de cada pestaña (null = el de origen; en Historial, fecha descendente). */
  orden: Record<Pestana, { col: string, dir: 1 | -1 } | null> = { operaciones: null, historial: null };

  private posicionesBroker: any[] = [];
  private pendientesBroker: any[] = [];
  private historialBroker: FilaHistorial[] = [];
  private ordenesBroker: any[] = [];
  private colaCruda: any[] = [];
  private brokerOffsetMin: number | null = null;
  /** orderId de la entrada Filled por isooper (companyName) = ticket de la posición. */
  private ticketPorIso = new Map<string, string>();
  /** Origen de la orden de entrada de cada posición, por isooper. */
  private origenPorIso = new Map<string, string>();
  /** Órdenes SL (STP) y TP (LMT) por posición (isooper|A/B), sacadas del order_ref/cOID. */
  private ocoPorPosicion = new Map<string, { sl?: any, tp?: any }>();
  /** Operaciones transformadas de la cartera y su ENTRADA por posición (isooper|A/B) para los SL/TP. */
  private operacionesRaw: any[] = [];
  private operaciones: any[] = [];
  private entradasPorPosicion = new Map<string, any>();
  /** Salida del modelo no enviada al broker por posición (isooper|A/B): ver salidaService.operacionesAbiertas. */
  private salidasPendientes = new Map<string, SalidaPendiente>();
  private subscriptions = new Subscription();

  constructor(private _broker: BrokerService,
    private temaService: TemaService,
    private _sseService: SseService,
    private _cookieService: CookieService,
    private brokerTimeService: BrokerTimeService,
    private brokerAdapterFactory: BrokerAdapterFactory,
    private ordenesSync: OrdenesSyncService,
    private colaOrdenesService: ColaOrdenesService,
    private salidaService: SalidaOperacionBrokerService,
    private accionesOrden: AccionesOrdenBrokerService,
    public sltp: SlTpBrokerService,
    private datePipe: DatePipe,
    private dialog: MatDialog,
    private toastr: ToastrService) { }

  ngOnInit(): void {
    this.cargarColumnas();
    this.subscriptions.add(this._broker.valBrokOptimaWallet$.subscribe((data: any) => {
      if (!data?.nombre) return;
      this.brokOptimaWallet = data;
      this.indexarOperaciones();
      this.listOrdenes();
      if (data.tipoBroker === 'METAAPI') {
        const accountId = data.metaApiAccountId || data.nombre;
        this.brokerTimeService.getOffsetMinutes(accountId).subscribe(offsetMin => this.brokerOffsetMin = offsetMin);
      } else {
        this.brokerOffsetMin = null;
      }
    }));
    this.subscriptions.add(this._broker.valBrokValPos$.subscribe((datos: any) => {
      this.posicionesBroker = agregaTiporModelo(flattenArray(datos || []));
      this.posiciones = this.ordenar('operaciones', this.posicionesBroker, 'pos');
    }));
    this.subscriptions.add(this.colaOrdenesService.operaciones$.subscribe(ops => {
      this.operacionesRaw = ops || [];
      this.indexarOperaciones();
    }));
    // Mismo disparador que lista-ordenes: reiniciar el flag es inocuo si la otra ya lo hizo
    this.subscriptions.add(this._broker.valcargarListas$.subscribe(data => {
      if (data && this.brokOptimaWallet) {
        this._broker.updatecargarListas(false);
        this.listOrdenes();
      }
    }));
    if (this.mostrarCola) {
      this.subscriptions.add(this.colaOrdenesService.colaOrdenes$.subscribe(cola => {
        this.colaCruda = cola || [];
        this.clasificarOrdenes();
      }));
    }
    // Órdenes canceladas desde /broker-operations (otra pestaña)
    this.subscriptions.add(this.ordenesSync.cambios$.subscribe(idcartera => {
      if (this.brokOptimaWallet?.idcartera && String(this.brokOptimaWallet.idcartera) === idcartera) {
        this.listOrdenes();
      }
    }));
    this.subscriptions.add(this.temaService.valDarkTheme.subscribe((data: any) => this.isDark = data));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /** Los split-handles de /broker lo llaman al redimensionar; la tabla no usa scroll virtual. */
  actualizarViewport() { }

  recargar() {
    this.listOrdenes();
  }

  private get panelClass(): string {
    return this.isDark ? 'dark-theme' : 'light-theme';
  }

  private listOrdenes() {
    const w = this.brokOptimaWallet;
    if (!w?.idcartera) return;
    const licencia = this._cookieService.get('auth_token');
    this._sseService.apiPostNew('broker/listaordenes', { idcartera: w.idcartera, tipoBroker: w.tipoBroker }, licencia).subscribe({
      next: (data: any[]) => {
        this.ordenesBroker = data || [];
        this.clasificarOrdenes();
        this.ordenesCargadas.emit(this.ordenesBroker);
      }
    });
  }

  /** Reparte las órdenes en tickets de posición, pendientes (+ cola) e historial. */
  private clasificarOrdenes() {
    this.ticketPorIso.clear();
    this.origenPorIso.clear();
    this.ordenesBroker
      .filter(o => ESTADOS_FILLED.includes(o.status) && o.orderId && String(o.orderId) !== '0')
      .sort((a, b) => (Number(a.lastExecutionTime) || 0) - (Number(b.lastExecutionTime) || 0))
      // La más antigua por ID es la entrada; las posteriores serían salidas parciales
      .forEach(o => {
        if (this.ticketPorIso.has(o.companyName)) return;
        this.ticketPorIso.set(o.companyName, String(o.orderId));
        if (o.origen) this.origenPorIso.set(o.companyName, o.origen);
      });

    // Como en MetaTrader, el SL/TP no es una fila: se ve en las columnas S/L y T/P de su posición
    this.indexarOco();
    this.pendientesBroker = [...this.colaPendiente(), ...this.ordenesBroker.filter(o =>
      o.status && !ESTADOS_CERRADOS.includes(o.status) && !this.esOco(o))];

    // Sin estado = guardada pero nunca enviada: va al Historial para poder eliminarla
    this.historialBroker = this.agruparHistorial(this.ordenesBroker.filter(o => !o.status || ESTADOS_CERRADOS.includes(o.status)))
      .sort((a, b) => this.fechaOrden(b.entrada) - this.fechaOrden(a.entrada));
    this.aplicarOrden();
  }

  /**
   * Una fila por posición (isooper|A/B del order_ref/cOID): la entrada es la primera orden que no es
   * STP/LMT (la ejecutada si hay), el SL/TP el ejecutado o el último, y la salida la orden directa
   * posterior ejecutada o, si no hay, el SL/TP ejecutado.
   */
  private agruparHistorial(ordenes: any[]): FilaHistorial[] {
    const grupos = new Map<string, any[]>();
    ordenes.forEach(o => {
      const clave = this.clavePosicion(o) || 'orden|' + (o.idorden ?? o.cOID ?? o.orderId);
      const grupo = grupos.get(clave);
      if (grupo) grupo.push(o); else grupos.set(clave, [o]);
    });
    const ejecutada = (o: any) => ESTADOS_FILLED.includes(o?.status);
    return [...grupos].map(([clave, grupo]) => {
      const porFecha = [...grupo].sort((a, b) => this.fechaOrden(a) - this.fechaOrden(b));
      const directas = porFecha.filter(o => !this.esOco(o));
      const entrada = directas.find(ejecutada) ?? directas[0] ?? porFecha[0];
      const nivel = (lado: 'sl' | 'tp') => {
        const ocos = porFecha.filter(o => o !== entrada && this.esOco(o) && this.ladoOco(o) === lado);
        return ocos.find(ejecutada) ?? ocos[ocos.length - 1];
      };
      const sl = nivel('sl'), tp = nivel('tp');
      const porSl = ejecutada(sl), porTp = ejecutada(tp);
      const salida = [...directas].reverse().find(o => o !== entrada && ejecutada(o)) ?? (porSl ? sl : porTp ? tp : undefined);
      return { clave, entrada, sl, tp, salida, porSl, porTp, ordenes: grupo };
    });
  }

  // ── Columnas y orden ──────────────────────────────────────────────────────

  private cargarColumnas() {
    try {
      const ocultas = JSON.parse(localStorage.getItem(CLAVE_COLUMNAS) || '{}');
      (Object.keys(this.columnas) as Pestana[]).forEach(p =>
        Array.isArray(ocultas[p]) && this.columnas[p].forEach(c => c.visible = !ocultas[p].includes(c.id)));
    } catch { /* sin preferencias guardadas */ }
    this.actualizarVisibles();
  }

  toggleColumna(col: ColumnaMt) {
    col.visible = !col.visible;
    this.actualizarVisibles();
    try {
      const ocultas: Record<string, string[]> = {};
      (Object.keys(this.columnas) as Pestana[]).forEach(p =>
        ocultas[p] = this.columnas[p].filter(c => !c.visible).map(c => c.id));
      localStorage.setItem(CLAVE_COLUMNAS, JSON.stringify(ocultas));
    } catch { /* almacenamiento no disponible */ }
  }

  private actualizarVisibles() {
    (Object.keys(this.columnas) as Pestana[]).forEach(p => this.visibles[p] = this.columnas[p].filter(c => c.visible));
  }

  expandirColPanel() { this.colPanelOpen = true; }
  contraerColPanel() { this.colPanelOpen = false; }

  /** Clic en la cabecera: ascendente, descendente y vuelta al orden de origen. */
  ordenarPor(col: ColumnaMt) {
    if (!col.pos && !col.orden) return;
    const actual = this.orden[this.pestana];
    this.orden[this.pestana] = actual?.col !== col.id ? { col: col.id, dir: 1 } : actual.dir === 1 ? { col: col.id, dir: -1 } : null;
    this.aplicarOrden();
  }

  flechaOrden(col: ColumnaMt): string {
    const o = this.orden[this.pestana];
    return o?.col === col.id ? (o.dir === 1 ? '▲' : '▼') : '';
  }

  private aplicarOrden() {
    this.posiciones = this.ordenar('operaciones', this.posicionesBroker, 'pos');
    this.pendientes = this.ordenar('operaciones', this.pendientesBroker, 'orden');
    this.historial = this.ordenar('historial', this.historialBroker, 'orden');
  }

  private ordenar(pestana: Pestana, filas: any[], tipo: 'pos' | 'orden'): any[] {
    const o = this.orden[pestana];
    const valor = o && this.columnas[pestana].find(c => c.id === o.col)?.[tipo];
    if (!o || !valor) return filas;
    return [...filas].sort((a, b) => {
      const va = valor(a), vb = valor(b);
      const cmp = typeof va === 'number' && typeof vb === 'number'
        ? (Number.isFinite(va) ? va : 0) - (Number.isFinite(vb) ? vb : 0)
        : String(va ?? '').localeCompare(String(vb ?? ''));
      return cmp * o.dir;
    });
  }

  /** Órdenes de la cola de envío de esta cuenta, con la forma de una orden de listaordenes. */
  private colaPendiente(): any[] {
    const cuenta = this.brokOptimaWallet?.nombre;
    if (!this.mostrarCola || !cuenta) return [];
    return this.colaCruda
      .map(item => item?.orders?.[0])
      .filter(o => o && o.acctId === cuenta)
      .map(o => ({
        enCola: true, orderId: '—', ticker: o.ticker, orderType: o.orderType, side: o.side,
        price: o.price, quantity: o.quantity, stopLoss: o.stopLoss, takeProfit: o.takeProfit,
        status: 'En cola', cOID: o.cOID
      }));
  }

  private esOco(o: any): boolean {
    return esOrdenOco(o);
  }

  private ladoOco(o: any): 'sl' | 'tp' {
    return ladoOco(o);
  }

  /**
   * SL/TP de cada posición por su order_ref/cOID (<prefijo>-<isooper>:<A|B>-SL / -TP). Si hay varias
   * filas del mismo SL/TP (p. ej. una cancelada y su reemplazo), manda la viva.
   */
  private indexarOco() {
    this.ocoPorPosicion.clear();
    // Sin status = guardada pero nunca aceptada por el broker (p. ej. rechazada): no es un SL/TP vivo
    this.ordenesBroker.filter(o => this.esOco(o) && o.status).forEach(o => {
      const clave = this.clavePosicion(o);
      if (!clave) return;
      const lado = this.ladoOco(o);
      const oco = this.ocoPorPosicion.get(clave) ?? {};
      const actual = oco[lado];
      if (!actual || (ESTADOS_CERRADOS.includes(actual.status) && !ESTADOS_CERRADOS.includes(o.status))) oco[lado] = o;
      this.ocoPorPosicion.set(clave, oco);
    });
  }

  /** isooper|A/B de una orden según su order_ref/cOID ('' si no lo trae). */
  private clavePosicion(o: any): string {
    const ref = String(o.order_ref || o.cOID || '');
    const iso = isooperDeOrderRef(ref);
    return iso ? iso + '|' + sentidoDeOrderRef(ref) : '';
  }

  private fechaOrden(o: any): number {
    return Number(o.lastExecutionTime_r) || Number(o.fechaActualizacion) || Number(o.fechaRegistro) || 0;
  }

  private indexarOperaciones() {
    if (!this.brokOptimaWallet?.idcartera) return;
    this.operaciones = this.sltp.transformarOperaciones(this.operacionesRaw, this.brokOptimaWallet.idcartera);
    this.entradasPorPosicion.clear();
    // El isooper puede repetirse entre sentidos: la clave lleva A/B, como ocoPorPosicion
    this.operaciones.filter(op => op.tipoOperacion === 'ENTRADA').forEach(op => this.entradasPorPosicion.set(this.clavePos(op), op));
    this.salidasPendientes.clear();
    this.salidaService.operacionesAbiertas(this.operacionesRaw, this.brokOptimaWallet.idcartera)
      .filter(op => op.salidaPendiente)
      .forEach(op => this.salidasPendientes.set(this.clavePos(op), op.salidaPendiente));
  }

  // ── Celdas ────────────────────────────────────────────────────────────────

  origenPosicion(pos: any): string {
    return etiquetaOrigenOrden(this.origenPorIso.get(pos?.isooper));
  }

  ticket(pos: any): string {
    return this.ticketPorIso.get(pos?.isooper) ?? '';
  }

  fechaPosicion(pos: any): string {
    return Number(pos.lastExecutionTimer) > 0 ? this.formatFechaHora(+pos.lastExecutionTimer) : '';
  }

  /** Precio con 5 decimales en CFD y 2 en el resto; vacío si no hay valor. */
  precio(valor: any, fila: any): string {
    const n = Number(valor);
    return valor === null || valor === undefined || valor === '' || !Number.isFinite(n) || n === 0
      ? '' : this.sltp.formatPrice(n, fila);
  }

  sl(pos: any): string {
    return this.precioOco(pos, 'sl');
  }

  tp(pos: any): string {
    return this.precioOco(pos, 'tp');
  }

  /**
   * Precio del SL/TP realmente colocado. Manda la orden STP/LMT de listaordenes (cerrada = sin SL/TP);
   * sin orden registrada se usa la operación (id de orden del broker o precio STP/LMT), no el precio calculado.
   */
  private precioOco(pos: any, lado: 'sl' | 'tp'): string {
    const orden = this.ocoDe(pos, lado);
    if (orden) return ESTADOS_CERRADOS.includes(orden.status) ? '' : this.precio(Number(orden.price) || orden.auxPrice, pos);
    const op = this.entradasPorPosicion.get(this.clavePos(pos));
    if (lado === 'sl') {
      return op && this.salidaService.tieneSl(op) ? this.precio(Number(op.pricestp) > 0 ? op.pricestp : op.stoploss, pos) : '';
    }
    return op && this.salidaService.tieneTp(op) ? this.precio(Number(op.pricelmt) > 0 ? op.pricelmt : op.profit, pos) : '';
  }

  /** Señal de salida del modelo pendiente de enviar para esta posición, o null. */
  salidaPendiente(pos: any): SalidaPendiente | null {
    return this.salidasPendientes.get(this.clavePos(pos)) ?? null;
  }

  private clavePos(pos: any): string {
    return pos?.isooper + '|' + (pos?.bajista ? 'B' : 'A');
  }

  private ocoDe(pos: any, lado: 'sl' | 'tp'): any {
    return this.ocoPorPosicion.get(this.clavePos(pos))?.[lado];
  }

  /** Precio del SL/TP vivo de la posición (0 si no hay o está cerrado). */
  private precioOcoVivo(pos: any, lado: 'sl' | 'tp'): number {
    const orden = this.ocoDe(pos, lado);
    return orden && !ESTADOS_CERRADOS.includes(orden.status) ? Number(orden.price) || Number(orden.auxPrice) || 0 : 0;
  }

  /** orderId de la STP/LMT viva de la posición (undefined si no hay o está cerrada). */
  private idOcoVivo(pos: any, lado: 'sl' | 'tp'): string | undefined {
    const orden = this.ocoDe(pos, lado);
    const id = orden && !ESTADOS_CERRADOS.includes(orden.status) ? String(orden.orderId ?? '') : '';
    return id && id !== '0' ? id : undefined;
  }

  /** SL/TP cancelado de una posición abierta: su precio ('' si no quedó guardado); null si no está cancelado. */
  nivelCancelado(pos: any, lado: 'sl' | 'tp'): string | null {
    const orden = this.ocoDe(pos, lado);
    if (!orden || !ESTADOS_CANCELADOS.includes(orden.status)) return null;
    return this.precio(Number(orden.price) || orden.auxPrice, pos) || '';
  }

  /** S/L de una orden: el suyo adjunto o, si es la propia STP (SL de una posición), su precio. */
  slOrden(o: any): string {
    return this.esOco(o) ? (this.ladoOco(o) === 'sl' ? this.precio(Number(o.price) || o.auxPrice, o) : '') : this.precio(o.stopLoss, o);
  }

  tpOrden(o: any): string {
    return this.esOco(o) ? (this.ladoOco(o) === 'tp' ? this.precio(Number(o.price) || o.auxPrice, o) : '') : this.precio(o.takeProfit, o);
  }

  /** S/L de una fila del Historial: su orden STP o, si no hubo, el adjunto a la entrada. */
  slHistorial(f: FilaHistorial): string {
    return f.sl ? this.precio(Number(f.sl.price) || f.sl.auxPrice, f.entrada) : this.slOrden(f.entrada);
  }

  tpHistorial(f: FilaHistorial): string {
    return f.tp ? this.precio(Number(f.tp.price) || f.tp.auxPrice, f.entrada) : this.tpOrden(f.entrada);
  }

  volumen(o: any): any {
    return o.filledQuantity || o.quantity;
  }

  /** Precio ejecutado (el de la orden si aún no se ejecutó). */
  precioEjec(o: any): any {
    return Number(o.avgPrice) || o.price;
  }

  estadoHistorial(f: FilaHistorial): string {
    if (f.porSl) return 'Cerrada por S/L';
    if (f.porTp) return 'Cerrada por T/P';
    if (f.salida) return 'Cerrada';
    return f.entrada.status || 'Sin enviar';
  }

  /** Entrada no ejecutada (cancelada, rechazada o sin enviar): estado en rojo. */
  historialFallida(f: FilaHistorial): boolean {
    return !ESTADOS_FILLED.includes(f.entrada.status);
  }

  tipoOrden(o: any): string {
    return tipoOrdenMt(o);
  }

  /** Bajista de una posición o de una orden (sufijo :A/:B de su order_ref/cOID); null si no se sabe. */
  bajistaDe(fila: any): boolean | null {
    if (typeof fila?.bajista === 'boolean') return fila.bajista;
    const sentido = sentidoDeOrderRef(fila?.order_ref || fila?.cOID).toUpperCase();
    return sentido === 'B' ? true : sentido === 'A' ? false : null;
  }

  sentido(fila: any): string {
    const bajista = this.bajistaDe(fila);
    return bajista === null ? '' : bajista ? 'Bajista' : 'Alcista';
  }

  formatFechaHora(timestamp: number): string {
    return formatFechaHoraCotizacion(timestamp, this.brokOptimaWallet, this.brokerOffsetMin, this.datePipe, this.brokerTimeService);
  }

  trackPos = (_: number, p: any) => p.isooper + '|' + (p.bajista ? 'B' : 'A');
  trackOrden = (_: number, o: any) => o.idorden ?? o.cOID ?? o.orderId;
  trackHistorial = (_: number, f: FilaHistorial) => f.clave;

  seleccionar(fila: any) {
    this.filaSeleccionada = fila;
  }

  // ── Acciones ──────────────────────────────────────────────────────────────

  /** X de la posición: salida a mercado de su operación, cancelando el SL/TP (como Salir Fin de Día). */
  cerrarPosicion(pos: any, event?: MouseEvent) {
    event?.stopPropagation();
    const w = this.brokOptimaWallet;
    if (!w || this.cerrando.has(pos.isooper)) return;
    // Con la automatización activa el modelo haría también la salida
    if (this._cookieService.check('auto' + w.idcartera)) {
      this.toastr.warning('Detenga la automatización antes de cerrar posiciones a mano', 'Cerrar posición');
      return;
    }
    this.brokerAdapterFactory.puedeOperar(w.tipoBroker).subscribe(ok => {
      if (!ok) return;
      this.dialog.open(ConfirmDialogComponent, {
        width: '380px', height: '250px', panelClass: this.panelClass,
        data: { msj: `¿Cerrar en el broker la posición ${pos.nombreCompania} (${pos.isooper})? Se cancelarán sus órdenes SL/TP.`, icon: 'warning' }
      }).afterClosed().subscribe((confirmado: boolean) => {
        if (confirmado) this.ejecutarCierre(pos, w);
      });
    });
  }

  private ejecutarCierre(pos: any, w: any) {
    const licencia = this._cookieService.get('auth_token');
    this.cerrando.add(pos.isooper);
    const fin = (ok: boolean, msg: string) => {
      this.cerrando.delete(pos.isooper);
      if (ok) {
        this.toastr.success(msg, 'Cerrar posición');
        this._broker.updatecargarListas(true);
      } else {
        this.toastr.error(msg, 'Cerrar posición', { timeOut: 15000 });
      }
    };
    // Operaciones en fresco: la salida necesita los ids de STP/LMT actuales
    (this._sseService.apiGetWithAuth(`operaciones/${w.idcartera}`, licencia) as Observable<any[]>).subscribe({
      next: ops => {
        const abiertas = this.salidaService.operacionesAbiertas(ops, w.idcartera);
        const op = abiertas.find(o => o.isooper === pos.isooper && !!o.bajista === !!pos.bajista);
        if (!op) {
          fin(false, 'La posición no tiene una operación abierta en la cartera');
          return;
        }
        // IBKR: el neto esperado del conid incluye todas las abiertas
        this.salidaService.cerrarOperacion(op, w, licencia, { abiertas, libros: new Map() }, 'SALIDA_MANUAL').subscribe({
          next: r => fin(r.ok, r.ok ? `${pos.nombreCompania} cerrada` : r.msg),
          error: e => fin(false, e?.message ?? 'Error desconocido')
        });
      },
      error: () => fin(false, 'No se pudieron leer las operaciones de la cartera')
    });
  }

  /** X de la celda S/L o T/P (como en MetaTrader): quita ese nivel de la posición tras confirmar. */
  cancelarNivel(pos: any, lado: 'sl' | 'tp', event: MouseEvent) {
    event.stopPropagation();
    const nivel = lado === 'sl' ? 'Stop Loss' : 'Take Profit';
    this.dialog.open(ConfirmDialogComponent, {
      width: '380px', height: '250px', panelClass: this.panelClass,
      data: { msj: `¿Cancelar el ${nivel} de ${pos.nombreCompania} (${pos.isooper})?` }
    }).afterClosed().subscribe((confirmado: boolean) => {
      if (confirmado) this.accionSlTp(lado === 'sl' ? 'cancelarSL' : 'cancelarTP', pos);
    });
  }

  accionSlTp(accion: 'establecerSL' | 'actualizarSL' | 'cancelarSL' | 'establecerTP' | 'cancelarTP', pos: any = this.filaSeleccionada) {
    const entrada = this.entradasPorPosicion.get(this.clavePos(pos));
    if (!entrada) {
      this.toastr.warning('La posición no tiene una operación asociada en la cartera.');
      return;
    }
    const w = this.brokOptimaWallet;
    // Mandan las órdenes de listaordenes que pinta la celda: los pricestp/lmt de la operación pueden ser
    // de otra posición del mismo isooper o de una orden ya ejecutada. IBKR: orderidsalidastp/lmt pueden
    // venir a 0 aunque la STP/LMT esté viva (en MetaAPI esos campos son el positionId: no se tocan).
    const op = {
      ...entrada,
      pricestp: this.precioOcoVivo(pos, 'sl'),
      pricelmt: this.precioOcoVivo(pos, 'tp'),
      precioactual: Number(pos.cotizacion) || 0,
      ...(w.tipoBroker === 'METAAPI' ? {} : {
        orderidsalidastp: this.idOcoVivo(pos, 'sl') ?? entrada.orderidsalidastp,
        orderidsalidalmt: this.idOcoVivo(pos, 'tp') ?? entrada.orderidsalidalmt
      })
    };
    const ctx: SlTpContexto = {
      idcartera: w.idcartera,
      cartera: String(w.nombre).toUpperCase(),
      tipoBroker: w.tipoBroker ?? 'IBKR',
      metaApiAccountId: w.metaApiAccountId ?? '',
      operaciones: this.operaciones,
      panelClass: this.panelClass
    };
    this.sltp[accion]([op], ctx);
  }

  cancelarOrden(orden: any, event?: MouseEvent) {
    event?.stopPropagation();
    this.accionesOrden.cancelarOrden(orden, this.brokOptimaWallet, this.panelClass).subscribe({
      next: recargar => { if (recargar) this.listOrdenes(); },
      error: (err: any) => console.error('Error al cancelar la orden', err)
    });
  }

  editarOrden(orden: any) {
    this.accionesOrden.editarOrden(orden, this.brokOptimaWallet, this.panelClass);
  }

  /** Cancela todas las pendientes (no toca los SL/TP de las posiciones: no son pendientes). */
  cancelarTodas() {
    this.accionesOrden.cancelarTodas(this.pendientes, this.brokOptimaWallet, this.panelClass).subscribe({
      next: () => this.listOrdenes(),
      error: (err: any) => console.error('Error al cancelar las órdenes', err)
    });
  }

  // Menú de pendientes e Historial (como lista-ordenes): en una fila del Historial, Cancelar y Editar
  // van sobre su entrada (el servicio avisa si está cerrada) y Eliminar borra todas sus órdenes.

  private esFilaHistorial(fila: any): fila is FilaHistorial {
    return Array.isArray(fila?.ordenes);
  }

  menuCancelar() {
    const fila = this.filaSeleccionada;
    this.cancelarOrden(this.esFilaHistorial(fila) ? fila.entrada : fila);
  }

  menuEditar() {
    const fila = this.filaSeleccionada;
    this.editarOrden(this.esFilaHistorial(fila) ? fila.entrada : fila);
  }

  menuEliminar() {
    const fila = this.filaSeleccionada;
    if (this.esFilaHistorial(fila)) this.eliminarFila(fila); else this.eliminarOrden(fila);
  }

  eliminarOrden(orden: any) {
    this.accionesOrden.eliminarOrden(orden, this.panelClass).subscribe({
      next: ok => { if (ok) this.listOrdenes(); },
      error: (err: any) => console.error('Error al eliminar la orden', err)
    });
  }

  /** Borra de la BD todas las órdenes de la fila del Historial (entrada, SL/TP y salida). */
  eliminarFila(fila: FilaHistorial) {
    this.accionesOrden.eliminarOrdenes(fila.ordenes, fila.entrada.companyName || fila.entrada.ticker, this.panelClass).subscribe({
      next: ok => { if (ok) this.listOrdenes(); },
      error: (err: any) => console.error('Error al eliminar las órdenes', err)
    });
  }

  /**
   * Editar a mano el precio del SL/TP de una posición. IBKR: la STP/LMT es una orden real (diálogo de
   * edición normal). MetaAPI: es un atributo de la posición; el diálogo hace POSITION_MODIFY conservando
   * el otro nivel.
   */
  editarNivel(pos: any, lado: 'sl' | 'tp') {
    const w = this.brokOptimaWallet;
    const nivel = lado === 'sl' ? 'Stop Loss' : 'Take Profit';
    const precioActual = Number(this.precioOco(pos, lado));
    const viva = this.ocoDe(pos, lado);
    const esMetaApi = w?.tipoBroker === 'METAAPI';
    const orden = viva && !ESTADOS_CERRADOS.includes(viva.status) ? viva
      // MetaAPI sin fila de orden (SL/TP solo en la operación): basta con los datos de la posición
      : esMetaApi && precioActual > 0 ? {
        ticker: pos.nombreCompania, orderType: lado === 'sl' ? 'STP' : 'LMT', price: precioActual,
        quantity: pos.cantidadacciones, side: pos.bajista ? 'BUY' : 'SELL', secType: pos.secType, acctId: w.nombre, orderId: 0
      } : null;
    if (!orden) {
      this.toastr.warning(`Sin ${nivel} activo: use Establecer ${nivel}`, pos.nombreCompania);
      return;
    }
    const extra = esMetaApi ? {
      nivelPosicion: {
        lado, isooper: pos.isooper, bajista: !!pos.bajista, cuenta: String(w.nombre).toUpperCase(),
        ticker: orden.ticker || pos.nombreCompania, positionId: orden.orderId,
        otroPrecio: Number(this.precioOco(pos, lado === 'sl' ? 'tp' : 'sl')) || 0
      }
    } : {};
    this.accionesOrden.editarOrden(orden, w, this.panelClass, extra);
  }
}
